import { LoanCategory } from '../../../../../../../../../types/api';
import { BusinessRuleBase as BusinessRuleBaseGeneral } from '../../../../../../business/BusinessBase';
import { ApplicationFormValues } from '../../../LoanDetailTypes';
import { Ruleset } from '../interfaces/Ruleset';
import { RulesetResult } from './rules';
import loanFormModel from '../form_models/loanFormModel';
import {
  RELATED_PARTY_RELATIONSHIP,
  RELATED_PARTY_TYPES,
  DEFAULT_PROPERTY_OWNER_NAME,
} from '../../../../../../constants';
import IsFBO from './rules/IsFBO';

const {
  loanCategory,
  businessOwnershipStatus,
  systemPropertyAddress,
  systemPropertyAddressCity,
  systemPropertyAddressState,
  systemPropertyAddressZipCode,
  principalType,
  systemPropertyOwnerFirstName,
  systemPropertyOwnerLastName,
  systemPropertyOwnerBusinessName,
  systemPropertyRecordType,
} = loanFormModel.formField;

export class BusinessRuleBase extends BusinessRuleBaseGeneral {
  protected _fieldMapping: Record<string, Ruleset | undefined> = {};
  protected fieldMapping: Record<string, Ruleset | undefined> = {};

  constructor(data: Partial<ApplicationFormValues>) {
    super(data);
  }

  public isNonProfit(): boolean {
    return [
      LoanCategory.NPO_FBO,
      LoanCategory.NPO_EDUCATIONAL_INSTITUTE,
      LoanCategory.NPO_OTHER,
      LoanCategory.NPO_HOA,
    ].includes(this.data[loanCategory.name] as LoanCategory);
  }

  public isFbo(): boolean {
    return IsFBO.evaluate(this.data);
  }

  public getSystemLocationAddress(defaultAddress = ''): string {
    const sysAddress = this.data[systemPropertyAddress.name];
    const sysAddressCity = this.data[systemPropertyAddressCity.name];
    const sysAddressState = this.data[systemPropertyAddressState.name];
    const sysAddressZipCode = this.data[systemPropertyAddressZipCode.name];
    if (
      !sysAddress ||
      !sysAddressCity ||
      !sysAddressState ||
      !sysAddressZipCode
    )
      return defaultAddress;

    return `${sysAddress}, ${sysAddressCity}, ${sysAddressState} ${sysAddressZipCode}`;
  }

  public isRequired(fieldName: string): boolean {
    const ruleset = this.fieldMapping[fieldName];
    if (!ruleset) return false;
    return Boolean(ruleset.evaluate(this.data) & RulesetResult.REQUIRED);
  }

  public isVisible(fieldName: string): boolean {
    const ruleset = this.fieldMapping[fieldName];
    if (!ruleset) return false;
    return Boolean(ruleset.evaluate(this.data) & RulesetResult.VISIBLE);
  }

  public isRequiredForPrincipal(fieldName: string, principalId: any): boolean {
    const ruleset = this.fieldMapping[fieldName];
    if (!ruleset) return false;
    (ruleset as any)?.setPrincipalId?.(principalId);
    return Boolean(ruleset.evaluate(this.data) & RulesetResult.REQUIRED);
  }

  public isVisibleForPrincipal(fieldName: string, principalId: any): boolean {
    const ruleset = this.fieldMapping[fieldName];
    if (!ruleset) return false;
    (ruleset as any)?.setPrincipalId?.(principalId);
    return Boolean(ruleset.evaluate(this.data) & RulesetResult.VISIBLE);
  }

  public resetMapping(): void {
    if (!Object.keys(this._fieldMapping).length) {
      this._fieldMapping = { ...this.fieldMapping };
    }
    this.fieldMapping = { ...this._fieldMapping };
  }

  public addFieldMapping(fieldName: string, ruleset: Ruleset): void {
    this.fieldMapping[fieldName] = ruleset;
  }

  public getBusinessOwnershipStatus(): string[] {
    const ownershipStatus = this.data[businessOwnershipStatus.name];
    if (ownershipStatus) {
      return ownershipStatus;
    }
    return [];
  }

  // These following 2 functions are to be used only inside the Validations for the principals, since they will be in the right level and index already
  public isTrust(): boolean {
    return this.data[principalType.name] === RELATED_PARTY_TYPES.ENTITY;
  }

  public isPrincipal(): boolean {
    return this.data['relationship'] === RELATED_PARTY_RELATIONSHIP.PRINCIPAL;
  }

  public isMaturityPayment(): boolean {
    const maturityPayment = this.data['loan_version']?.maturity_payment_amount;

    return !!maturityPayment;
  }

  public isMilestonePayment(): boolean {
    // Selected_Milestone_Advance_Program__c can have Yes or the name of the program, so we need to check for the negative cases
    return (
      this.data['borrower_advance'] !== 'No' &&
      this.data['borrower_advance'] !== null &&
      this.data['borrower_advance'] !== undefined
    );
  }

  public getLandlordName(): string {
    const principal = this.data.principals?.find(
      (principal) =>
        principal.relationship === RELATED_PARTY_RELATIONSHIP.LANDLORD
    );
    const firstName = principal?.[systemPropertyOwnerFirstName.name] ?? '';
    const lastName = principal?.[systemPropertyOwnerLastName.name] ?? '';
    const principalType = principal?.[systemPropertyRecordType.name] ?? '';

    const businessName =
      principal?.[systemPropertyOwnerBusinessName.name] ?? '';

    if (
      Boolean(firstName && lastName) &&
      principalType === RELATED_PARTY_TYPES.INDIVIDUAL
    )
      return `${firstName} ${lastName}`;
    if (Boolean(businessName) && principalType === RELATED_PARTY_TYPES.ENTITY)
      return businessName;
    return DEFAULT_PROPERTY_OWNER_NAME;
  }

  /**
   * Returns the field's label, appending " (Optional)" when the field is not required per ruleset.
   * Use for document/field labels whose required state is determined by ruleset (e.g. tax documents).
   */
  public getLabelWithOptional(
    field: { name: string; getLabel: (...args: any[]) => string },
    ...getLabelArgs: any[]
  ): string {
    const base = field.getLabel(...getLabelArgs);
    return this.isRequired(field.name) ? base : `${base} (Optional)`;
  }

  public getLabelWithOptionalForPrincipal(
    field: { name: string; getLabel: (...args: any[]) => string },
    principalId: string,
    ...getLabelArgs: any[]
  ): string {
    const base = field.getLabel(...getLabelArgs);
    return this.isRequiredForPrincipal(field.name, principalId)
      ? base
      : `${base} (Optional)`;
  }
}
